import React from "react";
import { Star, MessageCircle, MapPin, Scissors } from "lucide-react";
import { BUSINESS, IMAGES } from "../constants";
import { useLanguage } from "../contexts/LanguageContext";

export const Hero = () => {
  const { t } = useLanguage();
  return (
    <section
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="YOUR CUT Barbershop Hamburg Interior"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D14]/85 via-[#0A0D14]/70 to-[#0A0D14]" />
        <div className="absolute inset-0 bg-[#0A0D14]/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-28 md:pt-40 pb-16 md:pb-24 min-h-[100svh] flex flex-col justify-center">
        <div
          className="inline-flex self-start items-center gap-2 rounded-full bg-[#E63946]/10 border border-[#E63946]/40 px-3 py-1.5 sm:px-4 sm:py-2 mb-6 md:mb-8 fade-up"
          data-testid="hero-walkin-badge"
        >
          <Scissors className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#E63946]" />
          <span className="text-[10px] sm:text-xs md:text-sm font-heading uppercase tracking-widest text-[#E63946]">
            {t.hero.walkin_badge}
          </span>
        </div>

        <p
          className="font-script text-white/70 text-base sm:text-xl md:text-2xl mb-3 md:mb-4 fade-up"
          style={{ animationDelay: "0.1s" }}
          data-testid="hero-sublabel"
        >
          {t.hero.sublabel}
        </p>

        <h1
          className="font-heading font-bold uppercase leading-[0.85] tracking-tighter text-white mb-5 md:mb-6 fade-up"
          style={{ animationDelay: "0.2s" }}
          data-testid="hero-title"
        >
          <span className="block text-[15vw] sm:text-7xl md:text-8xl lg:text-9xl">
            <span className="text-[#E63946]">Y</span>OUR{" "}
            C<span className="text-[#E63946]">UT</span>
          </span>
        </h1>

        <h2
          className="font-heading uppercase text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-white/90 max-w-5xl leading-[1.05] mb-6 md:mb-8 fade-up"
          style={{ animationDelay: "0.3s" }}
          data-testid="hero-tagline"
        >
          {t.hero.tagline}{" "}
          <span className="text-[#E63946]">{t.hero.tagline_accent}</span>
        </h2>

        <p
          className="text-sm sm:text-base md:text-lg text-white/70 max-w-2xl leading-relaxed mb-8 md:mb-10 fade-up"
          style={{ animationDelay: "0.4s" }}
          data-testid="hero-description"
        >
          {t.hero.description}
        </p>

        <div
          className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-10 md:mb-12 fade-up"
          style={{ animationDelay: "0.5s" }}
        >
          <a
            href={BUSINESS.whatsappHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-3 bg-[#E63946] hover:bg-[#D92D39] text-white font-heading uppercase tracking-wider px-6 sm:px-7 py-3.5 sm:py-4 rounded-full transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(230,57,70,0.4)]"
            data-testid="hero-whatsapp-cta"
          >
            <MessageCircle className="w-5 h-5" />
            <span>{t.hero.cta_primary}</span>
          </a>
          <button
            onClick={() =>
              document
                .getElementById("kontakt")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="inline-flex items-center justify-center gap-3 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 text-white font-heading uppercase tracking-wider px-6 sm:px-7 py-3.5 sm:py-4 rounded-full transition-all duration-300"
            data-testid="hero-directions-cta"
          >
            <MapPin className="w-5 h-5" />
            <span>{t.hero.cta_secondary}</span>
          </button>
        </div>

        <div
          className="flex items-center gap-3 fade-up"
          style={{ animationDelay: "0.6s" }}
          data-testid="hero-rating"
        >
          <div className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className="w-4 h-4 fill-[#E63946] text-[#E63946]"
              />
            ))}
          </div>
          <span className="text-xs sm:text-sm text-white/70 font-body">
            {t.hero.rating}
          </span>
        </div>
      </div>
    </section>
  );
};
